import InlineNoteText from './InlineNoteText';
import NoteReviewEye from './NoteReviewEye';
import {resourceNoteReviewState} from '../../utils/resourceReview';
import {addPlayerMentions} from './playerMentions';
// E:\new-react-project\the-ultimate-guide\src\components\Review\NotesDisplay.js

import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  collection,
  onSnapshot,
  updateDoc,
  arrayUnion,
  arrayRemove,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db, auth, storage } from "../../Security/firebase-config";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { Pencil, Reply, Trash2 } from "lucide-react";
import { useTeam } from "../../context/TeamContext";

import MentionMenu from "./MentionMenu";
import DeleteHighlightConfirm from "../core-components/DeleteHighlightConfirm";
import ReplyInputBox from "./ReplyInputBox";
import ImageModal from "../core-components/ImageModal";

import "./NotesDisplay.css";

import { ensureActiveTeamClaim } from "../../utils/authClaims";
import {getDisplayNoteMentions} from "./noteMentionPresentation";

/* ──────────────────────────────────────────────────────────────
 * CDN helper (works in CRA and Vite)
 * ────────────────────────────────────────────────────────────── */
const getCdnDomain = () => {
  const fromCra =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_CDN_MEDIA_DOMAIN) ||
    "";
  let fromVite = "";
  try {
    fromVite =
      (typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.VITE_CDN_MEDIA_DOMAIN) ||
      "";
  } catch {}
  return fromCra || fromVite || "";
};

const toCdnUrl = (path, fallbackUrl) => {
  const cdn = getCdnDomain(); // e.g. "media.valovate.com"
  if (cdn && path) return `https://${cdn}/${encodeURI(path)}`;
  return fallbackUrl;
};

// same as NoteInputBox
const normalizeRole = (raw) => {
  const r = (raw || "player").toString().trim().toLowerCase();
  if (r === "subs" || r === "substitute" || r === "substitutes") return "sub";
  if (r === "manger" || r === "managers") return "manager";
  if (r === "spectators") return "spectator";
  if (r === "players") return "player";
  if (r === "trialing" || r === "trials") return "trial";
  return r;
};

// ── Mention extraction helper (source of truth for Firestore "at" field) ──
const extractMentionsFromText = (text, { members, byRole, coachingStaff }) => {
  const out = new Set();
  const t = String(text || "");

  // 1) @Team (case-insensitive)
  if (/@team\b/i.test(t)) out.add("@Team");

  // 2) Group labels (case-insensitive)
  if (/@\s*coaching\s+staff\b/i.test(t)) {
    (coachingStaff || []).forEach((m) => out.add(`@${m.username}`));
  }
  if (/@\s*subs\b/i.test(t)) {
    (byRole?.sub || []).forEach((m) => out.add(`@${m.username}`));
  }
  if (/@\s*managers\b/i.test(t)) {
    (byRole?.manager || []).forEach((m) => out.add(`@${m.username}`));
  }

  // 3) Individual @username tokens (only keep real team usernames)
  const usernameSet = new Set(
    (members || []).map((m) => String(m.username || "").toLowerCase())
  );

  const tokens = t.match(/@([A-Za-z0-9_]+)/g) || [];
  for (const tok of tokens) {
    const uname = tok.slice(1).toLowerCase();
    if (usernameSet.has(uname)) {
      const canonical =
        (members || []).find((m) => m.username?.toLowerCase() === uname)
          ?.username || tok.slice(1);
      out.add(`@${canonical}`);
    }
  }

  return addPlayerMentions(t, byRole?.player, Array.from(out));
};

// ──────────────────────────────────────────────────────────────
// Match-time helpers
// videoTime = matchTime + syncOffset
// matchTime = videoTime - syncOffset
// ──────────────────────────────────────────────────────────────
const toMatchTime = (videoTimeSec, syncOffsetSec) =>
  (Number(videoTimeSec || 0) || 0) - (Number(syncOffsetSec || 0) || 0);

const toVideoTime = (matchTimeSec, syncOffsetSec) =>
  (Number(matchTimeSec || 0) || 0) + (Number(syncOffsetSec || 0) || 0);

// Back-compat: old notes used `timestamp`.
// New model: `matchTimeSec`.
const getNoteMatchTime = (note) => {
  if (typeof note?.matchTimeSec === "number") return note.matchTimeSec;
  if (typeof note?.timestamp === "number") return note.timestamp; // legacy fallback
  return 0;
};

const pad2 = (n) => String(n).padStart(2, "0");
const formatTime = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds || 0) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${h}:${pad2(m)}:${pad2(r)}`;
};

function formatDateTime(ts) {
  const millis =
    typeof ts?.toMillis === "function"
      ? ts.toMillis()
      : typeof ts?.toDate === "function"
        ? ts.toDate().getTime()
        : typeof ts === "string"
          ? Date.parse(ts)
          : typeof ts === "number" && Number.isFinite(ts)
            ? ts
            : null;
  if (!Number.isFinite(millis)) return "";
  const dateObj = new Date(millis);
  const day = dateObj.getDate();
  const monthName = dateObj.toLocaleString("default", { month: "long" });
  const year = dateObj.getFullYear();
  return `${day}/${monthName}/${year}`;
}

/* ──────────────────────────────────────────────────────────────
 * ReplyItem (MOVED OUTSIDE NotesDisplay to prevent remount storms)
 * ────────────────────────────────────────────────────────────── */
function ReplyItem({ reply, username, replyDocRef, roleByUsername }) {
  const isOwner = reply.createdBy === username;
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(reply.text);

  useEffect(() => {
    setEditText(reply.text);
  }, [reply.text]);

  const rRef = replyDocRef(reply.id);

  const saveReply = async () => {
    await updateDoc(rRef, {
      text: editText,
      editedAt: serverTimestamp(),
    });
    setIsEditing(false);
  };

  const deleteReply = async () => {
    await deleteDoc(rRef);
  };

  const createdAtDate = reply.createdAt?.toDate ? reply.createdAt.toDate() : null;
  const createdAtStr = createdAtDate
    ? `${createdAtDate.getDate()}/${createdAtDate.toLocaleString("default", {
        month: "long",
      })}/${createdAtDate.getFullYear()}`
    : "";

  const viewersArr = Array.isArray(reply.viewers) ? reply.viewers : [];
  const viewedByMe = viewersArr.includes(username);

  return (
    <div className="reply-item">
      {isEditing ? (
        <>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={2}
          />
          <div className="reply-controls">
            <button onClick={saveReply}>Save</button>
            <button className={'app-cancel-control'} onClick={() => setIsEditing(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <p><InlineNoteText text={reply.text} roles={roleByUsername}/></p>
          <div className="reply-meta">
            <small>— {reply.createdBy}</small>

            <div className="eye-button-wrapper">
              {/* NOTE: reply viewed toggle is handled by parent NoteItem via updateDoc;
                  if you want it here, pass a handler prop. */}
              <button className={`reply-reviewed-btn ${viewedByMe ? "viewed" : ""}`}>👁</button>
              <div className="eye-tooltip">
                {viewersArr.length > 0 ? viewersArr.join(", ") : "No one has reviewed yet"}
              </div>
            </div>

            {createdAtStr && <small className="reply-date-inline">{createdAtStr}</small>}

            {isOwner && (
              <>
                <button
                  className="reply-edit-btn"
                  onClick={() => setIsEditing(true)}
                  title="Edit reply"
                  aria-label="Edit reply"
                >
                  <Pencil size={15} aria-hidden="true" />
                </button>
                <button
                  className="reply-delete-btn"
                  onClick={deleteReply}
                  title="Delete reply"
                  aria-label="Delete reply"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * NoteItem (MOVED OUTSIDE NotesDisplay + memo)
 * ────────────────────────────────────────────────────────────── */
const NoteItem = React.memo(function NoteItem({
  note,
  displayMode,
  activeNoteId,
  flashedNoteId,
  focusNoteId,
  username,
  roleByUsername,
  members,
  byRole,
  coachingStaff,
  mentionBaseItems,
  mentionClassFor,
  saveEdit,
  promptDelete,
  markViewed,
  onSeek,
  matchId,
  syncOffsetSec,
  mapId,
  videoId,
  replyDocRef,
  replies,
  setNoteEl,
  openMedia,
  mode,
  allowReplies = true,
}) {
  const currentUserUid = auth.currentUser?.uid || null;
  const canDeleteNote = !!currentUserUid && note.createdByUid === currentUserUid;
  const displayMentions = useMemo(() => getDisplayNoteMentions(note.text, note.at, [
    {label: "@Players", mentions: (byRole?.player || []).map(member => `@${member.username}`)},
    {label: "@ Coaching Staff", mentions: (coachingStaff || []).map(member => `@${member.username}`)},
    {label: "@ Subs", mentions: (byRole?.sub || []).map(member => `@${member.username}`)},
    {label: "@ Managers", mentions: (byRole?.manager || []).map(member => `@${member.username}`)},
  ]), [note.text, note.at, coachingStaff, byRole]);

  const [showReplyBox, setShowReplyBox] = useState(false);

  // LOCAL EDIT STATE
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(note.text);
  const [editTags, setEditTags] = useState(Array.isArray(note.tags) ? note.tags.join(", ") : "");
  const [editImportance, setEditImportance] = useState(note.importance);

  // keep local edit fields in sync if note changes from firestore
  useEffect(() => {
    setEditText(note.text);
    setEditTags(Array.isArray(note.tags) ? note.tags.join(", ") : "");
    setEditImportance(note.importance);
  }, [note.id, note.text, note.importance, note.tags]);

  // ── Inline mention popover (EDIT MODE) ──
  const editTextareaRef = useRef(null);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [editMenuPos, setEditMenuPos] = useState({ top: 0, left: 0 });
  const [editActiveIndex, setEditActiveIndex] = useState(0);
  const [editTrigger, setEditTrigger] = useState({ start: -1, query: "" });

  const editSuggestions = useMemo(() => {
    const q = (editTrigger.query || "").trim().toLowerCase();
    if (!q) return mentionBaseItems;
    return mentionBaseItems.filter((it) =>
      it.label.replace(/^@ ?/, "").toLowerCase().startsWith(q)
    );
  }, [mentionBaseItems, editTrigger.query]);

  const updateEditMenuPosition = () => {
    const ta = editTextareaRef.current;
    if (!ta) return;

    const parent = ta.closest(".note-item");
    if (!parent) return;

    const taRect = ta.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    setEditMenuPos({
      top: taRect.bottom - parentRect.top + 6,
      left: taRect.left - parentRect.left + 12,
    });
  };

  const onEditTextChangeInline = (e) => {
    const val = e.target.value;
    setEditText(val);

    const caret = e.target.selectionStart || 0;
    const uptoCaret = val.slice(0, caret);
    const match = uptoCaret.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);

    if (match) {
      const q = match[1];
      const atPos = caret - (q.length + 1);
      setEditTrigger({ start: atPos, query: q });
      setEditMenuOpen(true);
      setEditActiveIndex(0);
      updateEditMenuPosition();
    } else {
      setEditMenuOpen(false);
      setEditTrigger({ start: -1, query: "" });
    }
  };

  const applyEditPick = (item) => {
    const ta = editTextareaRef.current;
    if (!ta || editTrigger.start < 0) return;

    const before = editText.slice(0, editTrigger.start);
    const after = editText.slice(ta.selectionStart);

    let insertText = "";
    if (item.kind === "team") insertText = "@Team";
    else if (item.kind === "group") insertText = item.label;
    else insertText = `@${item.payload.username}`;

    const next = `${before}${insertText} ${after}`;
    setEditText(next);

    const newPos = (before + insertText + " ").length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    });

    setEditMenuOpen(false);
    setEditTrigger({ start: -1, query: "" });
  };

  const onEditKeyDownInline = (e) => {
    if (!editMenuOpen) return;
    if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) e.preventDefault();

    if (e.key === "ArrowDown") {
      setEditActiveIndex((i) => Math.min(i + 1, editSuggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      setEditActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setEditMenuOpen(false);
    } else if (e.key === "Enter" || e.key === "Tab") {
      const pick = editSuggestions[editActiveIndex] || editSuggestions[0];
      if (pick) applyEditPick(pick);
    }
  };

  const isActive = displayMode === "scroller" && note.id === activeNoteId;
  const isFlashing = displayMode === "scroller" && note.id === flashedNoteId;

  const noteMatchTimeSec = getNoteMatchTime(note);
  const seekVideoTimeSec = matchId ? toVideoTime(noteMatchTimeSec, syncOffsetSec) : noteMatchTimeSec;

  // mark a single reply viewed/unviewed
  const markReplyViewed = async (reply) => {
    if (!allowReplies) return;
    const rRef = replyDocRef(reply.id);
    const arr = Array.isArray(reply.viewers) ? reply.viewers : [];
    const updated = arr.includes(username)
      ? arr.filter((u) => u !== username)
      : [...arr, username];
    await updateDoc(rRef, { viewers: updated });
  };

  return (
    <div
      ref={(el) => setNoteEl(note.id, el)}
      className={`note-item priority-${note.importance} ${note.id===focusNoteId?'note-link-target':''} ${isActive ? "note-active" : ""} ${
        isFlashing ? "flash-border" : ""
      }`}
    >
      {isEditing ? (
        <>
          <textarea
            ref={editTextareaRef}
            className="note-edit-textarea"
            value={editText}
            onChange={onEditTextChangeInline}
            onKeyDown={onEditKeyDownInline}
            onFocus={updateEditMenuPosition}
            rows={3}
          />

          <MentionMenu
            items={editSuggestions}
            open={editMenuOpen}
            style={editMenuPos}
            activeIndex={editActiveIndex}
            onHoverIndex={(i) => setEditActiveIndex(i)}
            onPick={applyEditPick}
          />

          <input
            type="text"
            placeholder="Tags (comma-separated)"
            value={editTags}
            onChange={(e) => setEditTags(e.target.value)}
          />
          <div className="importance-selector">
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                onClick={() => setEditImportance(n)}
                className={editImportance === n ? "selected" : ""}
              >
                {"!".repeat(n)}
              </span>
            ))}
          </div>

          <div className="edit-controls">
            <button
              className="save-btn"
              onClick={async () => {
                const newTagsArr = editTags
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean);

                const computedMentions = extractMentionsFromText(editText, {
                  members,
                  byRole,
                  coachingStaff,
                });

                await saveEdit(note.id, {
                  text: editText,
                  at: computedMentions,
                  tags: newTagsArr,
                  importance: editImportance,
                });

                setIsEditing(false);
              }}
            >
              Save
            </button>

            <button
              className="cancel-btn app-cancel-control"
              onClick={() => {
                setEditText(note.text);
                setEditTags(Array.isArray(note.tags) ? note.tags.join(", ") : "");
                setEditImportance(note.importance);
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p><InlineNoteText text={note.text} roles={roleByUsername}/></p>

          <small>
            <span className="mentions">
              {displayMentions.map((m, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ", "}
                  <span className={`mention-tag ${mentionClassFor(m)}`}>{m}</span>
                </React.Fragment>
              ))}
            </span>
            {" — "}
            {(note.tags || []).join(", ")}
            {" — "}
            {"!".repeat(note.importance || 1)}
          </small>

          {note.createdBy && note.createdAt && (
            <div className="note-created-info">
              <small>
                Created by {note.createdBy} on {formatDateTime(note.createdAt)}
              </small>
            </div>
          )}

          {note.editedBy && (
            <div className="note-edited-info">
              <small>
                Edited by {note.editedBy}
                {note.editedAt ? ` on ${formatDateTime(note.editedAt)}` : ""}
              </small>
            </div>
          )}

          {note.mediaTitle && (
            <div className="note-media-wrapper">
              <button
                className="note-media-title-btn"
                onClick={() => openMedia(toCdnUrl(note.mediaStoragePath, note.mediaURL))}
              >
                <span className="media-icon">📷</span>
                <span className="media-title-text">{note.mediaTitle}</span>
              </button>
            </div>
          )}

          <div className="note-actions" style={{ marginTop: "0.5rem" }}>
            <NoteReviewEye note={note} uid={currentUserUid} username={username} role={roleByUsername?.[String(username||'').toLowerCase()]||'player'} onToggle={markViewed}/>

            <button
              className={`note-timestamp-btn ${isActive ? "active-timestamp" : ""}`}
              onClick={() => onSeek(seekVideoTimeSec)}
              title={matchId ? "Go to this match moment (current POV)" : "Go to this time"}
            >
              {formatTime(noteMatchTimeSec)}
            </button>

            <div className="note-right-actions">
              {allowReplies && (
                <button
                  className="reply-btn note-action-btn"
                  onClick={() => setShowReplyBox((v) => !v)}
                  title="Reply"
                  aria-label="Reply"
                >
                  <Reply size={15} aria-hidden="true" />
                </button>
              )}

              <button
                className="NotesDispaly-edit-btn note-action-btn"
                onClick={() => setIsEditing(true)}
                title="Edit Note"
                aria-label="Edit note"
              >
                <Pencil size={15} aria-hidden="true" />
              </button>

              {canDeleteNote && (
                <button
                  className="NotesDispaly-delete-btn note-action-btn"
                  onClick={() => promptDelete(note.id)}
                  title="Delete Note"
                  aria-label="Delete note"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {allowReplies && showReplyBox && (
            <ReplyInputBox
              mapId={mapId}
              matchId={matchId} // ReplyInputBox should support this path when present
              videoId={videoId} // legacy support
              noteId={note.id}
              onReplyAdded={() => setShowReplyBox(false)}
              onCancel={() => setShowReplyBox(false)}
            />
          )}

          {allowReplies && Array.isArray(replies) && replies.length > 0 && (
            <div className="replies-list">
              {replies.map((reply) => (
                <div key={reply.id}>
                  {/* reply viewed toggle button (kept in NoteItem so it has access to markReplyViewed) */}
                  <ReplyItem reply={reply} username={username} replyDocRef={replyDocRef} roleByUsername={roleByUsername}/>
                  <div className="reply-inline-actions">
                    <button
                      className={`reply-reviewed-btn ${
                        Array.isArray(reply.viewers) && reply.viewers.includes(username) ? "viewed" : ""
                      }`}
                      onClick={() => markReplyViewed(reply)}
                      title="Toggle viewed"
                    >
                      👁
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default function NotesDisplay({
  mapId,

  // NEW (match-based)
  matchId, // notes live under matches/{matchId}/notes
  syncOffsetSec = 0,

  // OLD (video-based) - backward compatibility
  videoId,

  currentTime, // VIDEO time
  onSeek, // expects VIDEO time

  displayMode, // 'list' or 'scroller'
  isPlaying,
  autoScroll,
  onDisableAuto,
  filters = {
    filterMentions: [],
    filterImportance: [],
    filterTags: [],
    hideReviewedByMe: false,
    currentUser: "",
  },
  autoScrollToNewest = false,
  focusNoteId = null,
  onlyNoteId = null,
  notesOverride = null,
  onSaveNoteEdit,
  onDeleteNote,
  onToggleNoteViewed,
  allowReplies = true,
}) {
  const { teamId, username } = useTeam();
  const [notes, setNotes] = useState([]);
  const [allReplies, setAllReplies] = useState([]);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const controlledNotes = Array.isArray(notesOverride) ? notesOverride : null;

  const containerRef = useRef(null);
  const noteRefs = useRef({});
  const isProgrammaticScroll = useRef(false);
  const settleTimerRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  const lastFlashedNoteIdRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  const [activeTimestamp, setActiveTimestamp] = useState(null);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [flashedNoteId, setFlashedNoteId] = useState(null);

  // image modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState("");

  const openMedia = useCallback((src) => {
    setModalSrc(src);
    setModalOpen(true);
  }, []);

  const setNoteEl = useCallback((noteId, el) => {
    if (!noteId) return;
    noteRefs.current[noteId] = el;
  }, []);

  const watchUntilScrollSettles = useCallback((containerEl) => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    settleTimerRef.current = setTimeout(() => {
      if (!containerEl) {
        isProgrammaticScroll.current = false;
        return;
      }
      const now = containerEl.scrollTop;
      if (Math.abs(now - lastScrollTopRef.current) <= 1) {
        isProgrammaticScroll.current = false;
        settleTimerRef.current = null;
      } else {
        lastScrollTopRef.current = now;
        watchUntilScrollSettles(containerEl);
      }
    }, 180);
  }, []);

  const beginProgrammaticScroll = useCallback((containerEl) => {
    if (!containerEl) return;
    isProgrammaticScroll.current = true;
    lastScrollTopRef.current = containerEl.scrollTop;
    watchUntilScrollSettles(containerEl);
  }, [watchUntilScrollSettles]);

  const mode = matchId ? "match" : "video";

  // match-time "now"
  const currentMatchTimeSec = useMemo(() => {
    if (matchId) return toMatchTime(currentTime || 0, syncOffsetSec);
    return Number(currentTime || 0) || 0;
  }, [matchId, currentTime, syncOffsetSec]);

  useEffect(() => {
    if (displayMode !== "scroller") {
      setFlashedNoteId(null);
      lastFlashedNoteIdRef.current = null;
      return;
    }
    if (!activeNoteId) return;
    if (lastFlashedNoteIdRef.current === activeNoteId) return;

    lastFlashedNoteIdRef.current = activeNoteId;
    setFlashedNoteId(activeNoteId);

    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => {
      setFlashedNoteId(null);
      flashTimeoutRef.current = null;
    }, 900);
  }, [activeNoteId, displayMode]);

  // members / roles for mentions
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!teamId) return;
    (async () => {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) return;

      const data = teamSnap.data() || {};
      const memberIds = data.members || [];
      const teamRoles = data.teamRoles || {};

      const rows = await Promise.all(
        memberIds.map(async (uid) => {
          const uRef = doc(db, "uids", uid);
          const uSnap = await getDoc(uRef);
          if (!uSnap.exists()) return null;
          const uname = uSnap.data().username;
          if (!uname) return null;
          return {
            uid,
            username: uname,
            roleLower: normalizeRole(teamRoles[uid] || "player"),
          };
        })
      );

      setMembers(rows.filter(Boolean));
    })();
  }, [teamId]);

  const byRole = useMemo(() => {
    const base = {
      player: [],
      sub: [],
      analyst: [],
      coach: [],
      manager: [],
      spectator: [],
      trial: [],
    };
    members.forEach((m) => {
      (base[m.roleLower] ||= []).push(m);
    });
    Object.keys(base).forEach((k) =>
      base[k].sort((a, b) => a.username.localeCompare(b.username))
    );
    return base;
  }, [members]);

  const coachingStaff = useMemo(
    () =>
      [...byRole.analyst, ...byRole.coach].sort((a, b) =>
        a.username.localeCompare(b.username)
      ),
    [byRole.analyst, byRole.coach]
  );

  const roleByUsername = useMemo(() => {
    const map = {};
    members.forEach((m) => {
      if (!m?.username) return;
      map[m.username.toLowerCase()] = m.roleLower || "player";
    });
    return map;
  }, [members]);

  const mentionClassFor = useCallback(
    (rawTag) => {
      const t = (rawTag || "").toLowerCase().trim();

      if (t === "@team") return "team";
      if (t === "@players") return "role role-player";
      if (t === "@ coaching staff") return "group coaching";
      if (t === "@ subs") return "group subs";
      if (t === "@ managers") return "group managers";

      const roleToken = t.replace(/^@/, "");
      const knownRoles = new Set(["manager", "analyst", "coach", "player", "sub", "trial", "spectator"]);
      if (knownRoles.has(roleToken)) return `role role-${roleToken}`;

      const usernameToken = roleToken;
      const role = roleByUsername[usernameToken] || "player";
      return `user role role-${role}`;
    },
    [roleByUsername]
  );

  const baseItems = useMemo(() => {
    let id = 0;
    const items = [];
    items.push({ id: id++, label: "@Players", kind: "group", group: "players", payload: {members: byRole.player} });

    byRole.player.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    if (coachingStaff.length) {
      items.push({
        id: id++,
        label: "@ Coaching Staff",
        kind: "group",
        group: "coaching",
        payload: { members: coachingStaff },
      });
    }

    if (byRole.sub.length) {
      items.push({
        id: id++,
        label: "@ Subs",
        kind: "group",
        group: "subs",
        payload: { members: byRole.sub },
      });
    }
    byRole.sub.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    if (byRole.manager.length) {
      items.push({
        id: id++,
        label: "@ Managers",
        kind: "group",
        group: "managers",
        payload: { members: byRole.manager },
      });
    }
    byRole.manager.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    byRole.spectator.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    byRole.trial.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    return items;
  }, [byRole, coachingStaff]);

  const notesColRef = useMemo(() => {
    if (!teamId || !mapId) return null;
    if (mode === "match") {
      return collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "notes");
    }
    if (!videoId) return null;
    return collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "notes");
  }, [teamId, mapId, mode, matchId, videoId]);

  const repliesColRef = useMemo(() => {
    if (!teamId || !mapId) return null;
    if (mode === "match") {
      return collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "allReplies");
    }
    if (!videoId) return null;
    return collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "allReplies");
  }, [teamId, mapId, mode, matchId, videoId]);

  const noteDocRef = useCallback(
    (noteId) => {
      if (mode === "match") {
        return doc(db, "teams", teamId, "maps", mapId, "matches", matchId, "notes", noteId);
      }
      return doc(db, "teams", teamId, "maps", mapId, "videos", videoId, "notes", noteId);
    },
    [mode, teamId, mapId, matchId, videoId]
  );

  const replyDocRef = useCallback(
    (replyId) => {
      if (mode === "match") {
        return doc(db, "teams", teamId, "maps", mapId, "matches", matchId, "allReplies", replyId);
      }
      return doc(db, "teams", teamId, "maps", mapId, "videos", videoId, "allReplies", replyId);
    },
    [mode, teamId, mapId, matchId, videoId]
  );

  // Firestore subscriptions
  useEffect(() => {
    if (controlledNotes) {
      setNotes(controlledNotes);
      return;
    }
    if (!notesColRef) {
      setNotes([]);
      return;
    }
    const unsubscribe = onSnapshot(
      notesColRef,
      (snapshot) => {
        const allNotes = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setNotes(allNotes);
      },
      (error) => {
        console.error("[NotesDisplay] notes onSnapshot error:", error);
        setNotes([]);
      }
    );
    return () => unsubscribe();
  }, [controlledNotes, notesColRef]);

  useEffect(() => {
    if (!allowReplies || controlledNotes) {
      setAllReplies([]);
      return;
    }
    if (!repliesColRef) {
      setAllReplies([]);
      return;
    }
    const qy = query(repliesColRef, orderBy("createdAt", "asc"));
    return onSnapshot(
      qy,
      (snap) => {
        setAllReplies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("[NotesDisplay] replies onSnapshot error:", error);
        setAllReplies([]);
      }
    );
  }, [allowReplies, controlledNotes, repliesColRef]);

  const repliesByNote = useMemo(() => {
    const m = new Map();
    for (const r of allReplies) {
      if (!r.noteId) continue;
      if (!m.has(r.noteId)) m.set(r.noteId, []);
      m.get(r.noteId).push(r);
    }
    return m;
  }, [allReplies]);

  // toggle viewed
  const markViewed = useCallback(
    async (note) => {
      if (controlledNotes) {
        await onToggleNoteViewed?.(note);
        return;
      }
      if (!teamId || !mapId) return;
      if (mode === "match" && !matchId) return;
      if (mode === "video" && !videoId) return;

      const nRef = noteDocRef(note.id);
      const uid=auth.currentUser?.uid;
      if(!uid&&!username)return;
      const viewed=resourceNoteReviewState(note,uid,username).viewed;
      const viewers=Array.isArray(note.viewers)?note.viewers:[];
      const legacyNames=[...new Set([username,uid,...viewers.filter(value=>String(value).toLowerCase()===String(username||'').toLowerCase())].filter(Boolean))];
      const updates={viewers:viewed?arrayRemove(...legacyNames):arrayUnion(username||uid)};
      if(uid)updates.viewedByUids=viewed?arrayRemove(uid):arrayUnion(uid);
      await updateDoc(nRef,updates);
    },
    [controlledNotes, mapId, matchId, mode, noteDocRef, onToggleNoteViewed, teamId, username, videoId]
  );

  // delete logic
  const promptDelete = useCallback((noteId) => setNoteToDelete(noteId), []);
  const cancelDelete = useCallback(() => setNoteToDelete(null), []);

  const actuallyDeleteNote = useCallback(async () => {
    if (!noteToDelete) return;
    if (controlledNotes) {
      await onDeleteNote?.(noteToDelete);
      setNoteToDelete(null);
      return;
    }
    if (!teamId || !mapId) return;
    if (mode === "match" && !matchId) return;
    if (mode === "video" && !videoId) return;

    const nRef = noteDocRef(noteToDelete);

    // read note to get storage path
    let mediaPath = null;
    try {
      const snap = await getDoc(nRef);
      if (snap.exists()) {
        mediaPath = snap.data()?.mediaStoragePath || null;
      }
    } catch (e) {
      console.warn("Failed to read note before delete:", e);
    }

    // delete all replies
    if (repliesColRef) {
      const repliesSnap = await getDocs(query(repliesColRef, where("noteId", "==", noteToDelete)));
      if (!repliesSnap.empty) {
        const batch = writeBatch(db);
        repliesSnap.docs.forEach((rDoc) => batch.delete(rDoc.ref));
        await batch.commit();
      }
    }

    // delete storage file
    if (mediaPath) {
      try {

        await ensureActiveTeamClaim(teamId);

        const sref = storageRef(storage, mediaPath);
        await deleteObject(sref);
      } catch (e) {
        console.warn("Storage delete warning for note media:", e?.message || e);
      }
    }

    await deleteDoc(nRef);
    setNoteToDelete(null);
  }, [controlledNotes, mapId, matchId, mode, noteDocRef, noteToDelete, onDeleteNote, repliesColRef, teamId, videoId]);

  // edit logic
  const saveEdit = useCallback(
    async (noteId, updatedFields) => {
      if (controlledNotes) {
        await onSaveNoteEdit?.(noteId, updatedFields);
        return;
      }
      if (!teamId || !mapId) return;
      if (mode === "match" && !matchId) return;
      if (mode === "video" && !videoId) return;

      const nRef = noteDocRef(noteId);
      await updateDoc(nRef, {
        ...updatedFields,
        editedBy: username,
        editedAt: serverTimestamp(),
      });
    },
    [controlledNotes, mapId, matchId, mode, noteDocRef, onSaveNoteEdit, teamId, username, videoId]
  );

  // sorting
  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      const aT = getNoteMatchTime(a);
      const bT = getNoteMatchTime(b);
      if (aT !== bT) return aT - bT;

      const ai = a.importance ?? 1;
      const bi = b.importance ?? 1;
      if (ai !== bi) return bi - ai;

      const aMillis =
        typeof a.createdAt?.toMillis === "function"
          ? a.createdAt.toMillis()
          : typeof a.createdAt?.toDate === "function"
            ? a.createdAt.toDate().getTime()
            : typeof a.createdAt === "string"
              ? Date.parse(a.createdAt) || 0
              : 0;
      const bMillis =
        typeof b.createdAt?.toMillis === "function"
          ? b.createdAt.toMillis()
          : typeof b.createdAt?.toDate === "function"
            ? b.createdAt.toDate().getTime()
            : typeof b.createdAt === "string"
              ? Date.parse(b.createdAt) || 0
              : 0;
      return aMillis - bMillis;
    });
  }, [notes]);

  // filters
  const filteredNotes = useMemo(() => {
    return sortedNotes.filter((note) => {
      if (onlyNoteId && note.id !== onlyNoteId) return false;
      const { filterMentions, filterImportance, filterTags, hideReviewedByMe, currentUser } = filters;

      if (filterMentions.length > 0) {
        const noteAtNormalized = (note.at || []).map((m) => m.toLowerCase().replace(/^@/, ""));
        const includesTeamOnly = filterMentions.length === 1 && filterMentions[0] === "team";
        const matchesAny = filterMentions.some((m) => noteAtNormalized.includes(m));
        const hasTeamTag = noteAtNormalized.includes("team");

        if (includesTeamOnly) {
          if (!hasTeamTag) return false;
        } else {
          if (!matchesAny && !hasTeamTag) return false;
        }
      }

      if (filterImportance.length > 0) {
        if (!filterImportance.includes(note.importance)) return false;
      }

      if (filterTags.length > 0) {
        const lowerTags = (note.tags || []).map((t) => String(t).toLowerCase());
        const hasAnyTag = filterTags.some((ft) => lowerTags.includes(ft.toLowerCase()));
        if (!hasAnyTag) return false;
      }

      if (hideReviewedByMe) {
        if ((note.viewers || []).includes(currentUser)) return false;
      }

      return true;
    });
  }, [sortedNotes, filters, onlyNoteId]);

  // NEW: auto-scroll to newest note when one is added (list mode)
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (!autoScrollToNewest) return;
    if (displayMode !== "list") return;

    const prev = prevCountRef.current;
    const next = filteredNotes.length;

    // Only react to additions
    if (next > prev) {
      const last = filteredNotes[next - 1];
      const containerEl = containerRef.current;

      if (last?.id && containerEl) {
        // Ensure the last note element exists before scrolling
        requestAnimationFrame(() => {
          const el = noteRefs.current[last.id];
          if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "end" });
          } else {
            // fallback
            containerEl.scrollTo({ top: containerEl.scrollHeight, behavior: "smooth" });
          }
        });
      }
    }

    prevCountRef.current = next;
  }, [autoScrollToNewest, displayMode, filteredNotes]);

  const currentSecond = Math.floor(currentMatchTimeSec || 0);
  const focusedTarget=useRef(null);
  useEffect(()=>{
    const key=JSON.stringify([teamId,mapId,matchId,videoId,focusNoteId]);
    if(!focusNoteId||focusedTarget.current===key)return;
    const element=noteRefs.current[focusNoteId],container=containerRef.current;
    if(!element||!container)return;
    const top=container.scrollTop+element.getBoundingClientRect().top-container.getBoundingClientRect().top-8;
    beginProgrammaticScroll(container);container.scrollTo?.({top,behavior:'smooth'});
    focusedTarget.current=key;
  },[focusNoteId,filteredNotes,teamId,mapId,matchId,videoId,beginProgrammaticScroll]);

  // auto-scroll active timestamp
  useEffect(() => {
    if (displayMode !== "scroller") {
      if (activeTimestamp !== null) setActiveTimestamp(null);
      if (activeNoteId !== null) setActiveNoteId(null);
      return;
    }

    const timestampsLE = Array.from(
      new Set(filteredNotes.map((n) => getNoteMatchTime(n)).filter((ts) => ts <= currentSecond))
    );

    if (timestampsLE.length === 0) {
      if (activeTimestamp !== null) setActiveTimestamp(null);
      return;
    }

    const newActive = Math.max(...timestampsLE);
    const firstInGroup = filteredNotes.find((n) => getNoteMatchTime(n) === newActive) || null;

    const shouldScrollNow =
      autoScroll &&
      firstInGroup &&
      containerRef.current &&
      noteRefs.current[firstInGroup.id] &&
      (newActive !== activeTimestamp || !isProgrammaticScroll.current);

    if (newActive !== activeTimestamp) {
      setActiveTimestamp(newActive);
      setActiveNoteId(firstInGroup ? firstInGroup.id : null);
    } else {
      if (!activeNoteId && firstInGroup) setActiveNoteId(firstInGroup.id);
    }

    if (shouldScrollNow) {
      const el = noteRefs.current[firstInGroup.id];
      const containerEl = containerRef.current;
      const cRect = containerEl.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      const delta = eRect.top - cRect.top;
      const desiredTop = containerEl.scrollTop + delta - 8;

      beginProgrammaticScroll(containerEl);
      containerEl.scrollTo({ top: desiredTop, behavior: "smooth" });
    }
  }, [currentSecond, displayMode, autoScroll, filteredNotes, activeTimestamp, activeNoteId, beginProgrammaticScroll]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        className="notes-display"
        onScroll={() => {
          if (displayMode === "scroller" && autoScroll && !isProgrammaticScroll.current) onDisableAuto();
        }}
        onWheel={() => {
          if (displayMode === "scroller" && autoScroll && !isProgrammaticScroll.current) onDisableAuto();
        }}
        onTouchStart={() => {
          if (displayMode === "scroller" && autoScroll && !isProgrammaticScroll.current) onDisableAuto();
        }}
        onKeyDown={(e) => {
          const navKeys = ["PageDown", "PageUp", "Home", "End", "ArrowUp", "ArrowDown", " "];
          if (navKeys.includes(e.key)) {
            if (displayMode === "scroller" && autoScroll && !isProgrammaticScroll.current) {
              onDisableAuto();
            }
          }
        }}
        tabIndex={0}
      >
        {filteredNotes.map((note) => (
          <NoteItem
            key={note.id}
            note={note}
            displayMode={displayMode}
            activeNoteId={activeNoteId}
            flashedNoteId={flashedNoteId}
            focusNoteId={focusNoteId}
            username={username}
            roleByUsername={roleByUsername}
            members={members}
            byRole={byRole}
            coachingStaff={coachingStaff}
            mentionBaseItems={baseItems}
            mentionClassFor={mentionClassFor}
            saveEdit={saveEdit}
            promptDelete={promptDelete}
            markViewed={markViewed}
            onSeek={onSeek}
            matchId={matchId}
            syncOffsetSec={syncOffsetSec}
            mapId={mapId}
            videoId={videoId}
            replyDocRef={replyDocRef}
            replies={repliesByNote.get(note.id) || []}
            setNoteEl={setNoteEl}
            openMedia={openMedia}
            mode={mode}
            allowReplies={allowReplies}
          />
        ))}

        {noteToDelete && (
          <DeleteHighlightConfirm
            message="Are you sure you want to delete this note?"
            onConfirm={actuallyDeleteNote}
            onCancel={cancelDelete}
          />
        )}
      </div>

      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        src={modalSrc}
        alt="Attached image"
      />
    </>
  );
}
